/**
 * WritingSessionRow — one writing-session row, shared by the dashboard tile
 * (WritingPracticeTile) and the full history page (WritingHistoryPage).
 *
 * Implements show-not-tell/TS-014 (T5, AC-TS014.4, AC-TS014.5). Extracted
 * verbatim from WritingPracticeTile's inline row markup (show-not-tell/
 * TS-012 T4) so both surfaces render IDENTICAL rows from a single source of
 * truth (TS-014 decision log DD-5) — including the US-011 reopen navigation
 * (tapping a row navigates to ROUTES.WRITING_PRACTICE?sessionId=<id>, which
 * WritingPracticePage.openSession() already handles; AC-TS014.5: not a new
 * viewer).
 *
 * Pure presentational component — no data fetching, no error handling of its
 * own (TO-LL-036's fail-safe behavior lives in the callers' EmptyState/
 * ErrorState branches, unchanged by this extraction).
 */

import { useNavigate } from 'react-router-dom';
import { formatRelativeTime } from '@/lib/formatters';
import { ROUTES } from '@/constants/routes';
import type { RecentWritingSession } from '@/types/writingPractice';

export interface WritingSessionRowProps {
  row: RecentWritingSession;
}

export function WritingSessionRow({ row }: WritingSessionRowProps) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() =>
        navigate(`${ROUTES.WRITING_PRACTICE}?sessionId=${encodeURIComponent(row.sessionId)}`)
      }
      className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-2xl border border-otter-sand bg-white px-4 py-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
      aria-label={`Open writing session from ${formatRelativeTime(row.createdAt)}`}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-otter-earth-800 capitalize">
          {row.anchorEmotion || 'Writing practice'}
        </p>
        <p className="mt-0.5 text-xs font-medium text-gray-500">
          {formatRelativeTime(row.createdAt)}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
        <span
          className={
            row.revealFired
              ? 'inline-flex items-center rounded-full bg-sky-100 px-2 py-1 text-xs font-medium text-sky-800'
              : 'inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800'
          }
        >
          {row.revealFired ? '💡 With a hint' : '✨ Showed it!'}
        </span>
        {row.concreteLayerCountAtFirstPass != null && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
            {row.concreteLayerCountAtFirstPass} details
          </span>
        )}
        {row.roundsToResolve > 1 && (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
            {row.roundsToResolve} rounds
          </span>
        )}
      </div>
    </button>
  );
}
